import { useSuspenseQuery } from '@apollo/client'
import { Navigate, Outlet, useLocation, useParams } from '@faceup/router'
import { usePromiseSuspense } from '@faceup/ui'
import { languageEnumToBasename } from '@faceup/utils'
import { Suspense } from 'react'
import { graphql } from '../../../__generated__'
import { FullScreenLoader } from '../../../Components/FullScreenLoader'
import { SurveyLayout } from '../../../Components/SurveyLayout/SurveyLayout'
import useRegion from '../../../utils/useRegion'
import { LanguageWrapper } from '../../LanguageWrapper'
import { SurveyProvider } from '../../SurveyProvider'
import { UiProvider } from '../../UiProvider'

const query = graphql(`
  query SurveyInitByShortIdQuery($shortId: GraphQLString!) {
    publicSurveyByShortId(shortId: $shortId) {
      id
      defaultLanguage
      institution {
        id
        type
        isTesting
        billing {
          id
          plan
        }
        config {
          id
          institutionName
          primaryColor
        }
        partner {
          id
          config {
            id
            primaryColor
          }
        }
      }
      ...SurveyLayout_survey
      ...SurveyProvider_survey
    }
  }
`)

/**
 * Element of the survey route branch (q/:shortId). Discovers the region, runs
 * the survey init query (app shell: layout + provider slices), forces the
 * survey's language, then hands the shell data top-down. Survey pages run their
 * own content queries below the Outlet.
 *
 * Web flow for now; the app client is wired in the mobile-isolation phase.
 */
export const SurveyWrapper = () => {
  const { language: languageParam, shortId } = useParams<'language' | 'shortId'>()
  const location = useLocation()
  const { discoverByShortlink } = useRegion()

  // Sets the module-level region before the regional query runs.
  const discovery = usePromiseSuspense(discoverByShortlink, [shortId ?? ''])

  const { data } = useSuspenseQuery(query, {
    variables: { shortId: shortId ?? '' },
  })

  const survey = data.publicSurveyByShortId
  if (!discovery || !survey) {
    return <Navigate to={routes => routes.home()} replace />
  }

  // Surveys are mono-language: always the survey's default language. Compare
  // basenames (not languages) so a garbage/unsupported first URL segment - which
  // convertBasenameToLanguage would map to the default - is normalized out of the
  // URL instead of left stuck there.
  const effectiveLanguage = survey.defaultLanguage
  const effectiveBasename = languageEnumToBasename(effectiveLanguage)
  if (effectiveBasename !== languageParam) {
    const segments = location.pathname.split('/')
    segments[1] = effectiveBasename
    const target = `${segments.join('/')}${location.search}`
    return <Navigate to={() => target} replace />
  }

  return (
    <LanguageWrapper language={effectiveLanguage}>
      <UiProvider
        primaryColor={
          survey.institution.config?.primaryColor ??
          survey.institution.partner?.config?.primaryColor ??
          undefined
        }
        institutionName={survey.institution.config?.institutionName ?? ''}
        mother={{
          type: survey.institution.type ?? null,
          isTesting: survey.institution.isTesting ?? null,
          plan: survey.institution.billing?.plan ?? null,
        }}
      >
        <SurveyProvider survey={survey}>
          <SurveyLayout survey={survey}>
            <Suspense fallback={<FullScreenLoader />}>
              <Outlet />
            </Suspense>
          </SurveyLayout>
        </SurveyProvider>
      </UiProvider>
    </LanguageWrapper>
  )
}
